import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  EyeIcon, 
  PencilIcon, 
  TrashIcon, 
  XCircleIcon,
  MapPinIcon,
  EllipsisHorizontalIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { formatDistanceToNow } from 'date-fns';
import { Opportunity } from '../../types';
import { useAppSelector } from '../../hooks/redux';
import Button from '../common/Button';
import ConfirmationModal from '../common/ConfirmationModal';

interface ManagementOpportunityCardProps {
  opportunity: Opportunity;
  onDelete?: (id: string) => void;
  onClose?: (id: string) => void;
  onRevive?: (id: string) => void;
}

const ManagementOpportunityCard: React.FC<ManagementOpportunityCardProps> = ({ 
  opportunity, 
  onDelete,
  onClose,
  onRevive
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const isHirer = user?.user_type === 'hirer';
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReviveModal, setShowReviveModal] = useState(false);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active': 
        return {
          bg: 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/30',
          pulse: true,
          label: 'Active'
        };
      case 'draft': 
        return {
          bg: 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/30',
          pulse: false,
          label: 'Draft'
        };
      case 'closed': 
        return {
          bg: 'bg-gray-50 text-gray-600 border-gray-100 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-800',
          pulse: false,
          label: 'Closed'
        };
      default: 
        return {
          bg: 'bg-gray-50 text-gray-600 border-gray-100 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-800',
          pulse: false,
          label: status.toUpperCase()
        };
    }
  };

  const statusConfig = getStatusConfig(opportunity.status);

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-150 dark:border-gray-800/85 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col h-full overflow-hidden">
        <div className="p-6 flex flex-col flex-1">
          {/* Header Row */}
          <div className="flex items-center justify-between mb-3.5">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusConfig.bg}`}>
              {statusConfig.pulse && (
                <span className="relative flex h-2 w-2 mr-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
              )}
              {statusConfig.label}
            </span>

            {/* Quick Actions Menu */}
            <Menu as="div" className="relative flex-shrink-0">
              <Menu.Button className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <EllipsisHorizontalIcon className="h-5 w-5" />
              </Menu.Button>
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <Menu.Items className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 focus:outline-none z-10 py-1 overflow-hidden">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to={`/opportunities/${opportunity.id}`}
                        className={`${active ? 'bg-gray-50 dark:bg-gray-800 text-black dark:text-white' : 'text-gray-700 dark:text-gray-300'} flex items-center px-4 py-2.5 text-xs font-semibold`}
                      >
                        <EyeIcon className="h-4 w-4 mr-2.5 text-gray-400" />
                        View Public Page
                      </Link>
                    )}
                  </Menu.Item>
                  {opportunity.status === 'draft' && (
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to={`/opportunities/${opportunity.id}/edit`}
                          className={`${active ? 'bg-gray-50 dark:bg-gray-800 text-black dark:text-white' : 'text-gray-700 dark:text-gray-300'} flex items-center px-4 py-2.5 text-xs font-semibold`}
                        >
                          <PencilIcon className="h-4 w-4 mr-2.5 text-gray-400" />
                          {isHirer ? 'Edit Requirement' : 'Edit Opportunity'}
                        </Link>
                      )}
                    </Menu.Item>
                  )}
                  {opportunity.status === 'active' && onClose && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setShowCloseModal(true)}
                          className={`${active ? 'bg-gray-50 dark:bg-gray-800 text-black dark:text-white' : 'text-gray-700 dark:text-gray-300'} flex w-full items-center px-4 py-2.5 text-xs font-semibold`}
                        >
                          <XCircleIcon className="h-4 w-4 mr-2.5 text-gray-400" />
                          {isHirer ? 'Close Requirement' : 'Close Opportunity'}
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  {onDelete && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => onDelete(opportunity.id)}
                          className={`${active ? 'bg-red-50 dark:bg-red-955/20 text-red-600 dark:text-red-400' : 'text-red-500'} flex w-full items-center px-4 py-2.5 text-xs font-semibold border-t border-gray-50 dark:border-gray-800/80`}
                        >
                          <TrashIcon className="h-4 w-4 mr-2.5" />
                          Delete
                        </button>
                      )}
                    </Menu.Item>
                  )}
                </Menu.Items>
              </Transition>
            </Menu>
          </div>

          {/* Title & Posted Time */}
          <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white line-clamp-1 mb-0.5" title={opportunity.title}>
            {opportunity.title}
          </h3>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium mb-4">
            Posted {formatDistanceToNow(new Date(opportunity.created_at), { addSuffix: true })}
          </p>

          {/* Metadata: Location & Budget */}
          <div className="space-y-2 mb-5">
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
              <span className="truncate">{opportunity.location}</span>
            </div>
            {opportunity.is_paid && opportunity.budget && (
              <div className="flex items-center text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
                <BanknotesIcon className="h-4 w-4 text-emerald-500 mr-2 flex-shrink-0" />
                <span>Rs. {opportunity.budget.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Analytics Stats Grid */}
          <div className="grid grid-cols-2 py-4 border-t border-b border-gray-100 dark:border-gray-800/80 mb-6 mt-auto">
            <div className="text-center border-r border-gray-105 dark:border-gray-800/80">
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">
                Applications
              </span>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {opportunity.application_count}
              </span>
            </div>
            <div className="text-center">
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block mb-1">
                Total Views
              </span>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                {opportunity.view_count}
                <span className="text-xs text-gray-450 dark:text-gray-500 font-medium ml-1">
                  ({opportunity.unique_view_count || 0})
                </span>
              </span>
            </div>
          </div>

          {/* Actions */}
          <div>
            {opportunity.status === 'active' ? (
              <Link to={`/opportunities/${opportunity.id}/applications`} className="block w-full">
                <Button variant="primary" className="w-full justify-center py-2.5 text-sm font-semibold">
                  Review Applications
                </Button>
              </Link>
            ) : opportunity.status === 'draft' ? (
              <Link to={`/opportunities/${opportunity.id}/edit`} className="block w-full">
                <Button variant="outline" className="w-full justify-center py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700">
                  Continue Editing
                </Button>
              </Link>
            ) : (
              <Button 
                variant="outline" 
                onClick={() => setShowReviveModal(true)}
                className="w-full justify-center py-2.5 text-sm font-semibold text-orange-600 dark:text-orange-400 border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/20"
              >
                {isHirer ? 'Revive Requirement' : 'Revive Opportunity'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={() => {
          onClose?.(opportunity.id);
          setShowCloseModal(false);
        }}
        title={isHirer ? "Close Requirement" : "Close Opportunity"}
        message={isHirer ? "Are you sure you want to close this requirement? It will no longer accept applications." : "Are you sure you want to close this opportunity? It will no longer accept applications."}
        confirmText={isHirer ? "Close Requirement" : "Close Opportunity"}
        type="danger"
      />

      <ConfirmationModal
        isOpen={showReviveModal}
        onClose={() => setShowReviveModal(false)}
        onConfirm={() => {
          onRevive?.(opportunity.id);
          setShowReviveModal(false);
        }}
        title={isHirer ? "Revive Requirement" : "Revive Opportunity"}
        message={isHirer ? "Are you sure you want to revive this requirement? It will be active for another 30 days." : "Are you sure you want to revive this opportunity? It will be active for another 30 days."}
        confirmText={isHirer ? "Revive Requirement" : "Revive Opportunity"}
        type="info"
      />
    </>
  );
};

export default ManagementOpportunityCard;
